// "Mein Tag" page — Microsoft To-Do "My Day"-style view.
// Shows today's planned items and overdue-soon suggestions from the store.
//
// Redesign-Paket 2: Titel, Datum und die vier Kopf-Aktionen (Tag zurück, Datum,
// Tag vor, „Tag planen (KI)") stehen jetzt in der EINEN App-Kopfzeile
// (`usePageHeader`), nicht mehr in einem selbst gebauten Seitenkopf.

import { useEffect, useRef, useState } from "preact/hooks";
import { useLocation } from "wouter-preact";
import {
  Plus,
  X,
  Check,
  ChevronLeft,
  ChevronRight,
  Circle,
  Sparkles,
  AlertTriangle,
} from "lucide-preact";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/Button";
import { PageShell } from "../components/ui/PageShell";
import { usePageHeader } from "../lib/pageHeader";
import { requireLabel } from "../lib/navigation";
import {
  useMyDay,
  setMyDayDate,
  refreshMyDay,
  addToMyDay,
  removeFromMyDay,
} from "../store/myDay";
import { togglePersonalTaskDone } from "../store/personalTasks";
import { aiPlanMyDay, ApiError } from "../lib/api";
import { formatDateShort, localTodayIso } from "../lib/format";
import { openPersonalTaskSig, openProjectTaskSig } from "../store/deepLinks";
import type { MyDayPlanItem, MyDayPlanResult } from "@juuplan/shared";
import { DragDropContext, Draggable, Droppable, type DropResult } from "@hello-pangea/dnd";
import { GripVertical } from "lucide-preact";
import { uxEnabled } from "../lib/ux";
import { reorderMyDay } from "../lib/uxApi";
import { applyReorder } from "../lib/myDayOrder";
import { NeedsYouPanel } from "../components/today/NeedsYouPanel";
import { openCapture } from "../store/capture";
import { TodayDetail } from "../components/today/TodayDetail";
import { DetailSheet } from "../components/taskDetail/DetailSheet";
import type { TaskRecord } from "../lib/taskRecord";
import type { MyDayItem } from "../store/myDay";

// A generated KI day plan survives reloads for this long (4 hours), keyed by the
// day it was computed for. Stored in localStorage so it persists per browser.
const PLAN_TTL_MS = 4 * 60 * 60 * 1000;
// v2: plan result gained a structured `backlog` array (was a single note string).
const PLAN_STORAGE_KEY = "juuplan.myDayPlan.v2";

interface StoredPlan {
  day: string;
  plan: MyDayPlanResult;
  addedKeys: string[];
  /** Generation time (ms epoch) — the 4h window counts from here, not reloads. */
  ts: number;
}

// Restore a still-fresh plan for `day`, or null (also evicting stale/foreign entries).
function loadStoredPlan(
  day: string
): { plan: MyDayPlanResult; addedKeys: Set<string>; ts: number } | null {
  try {
    const raw = localStorage.getItem(PLAN_STORAGE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as StoredPlan;
    if (s.day !== day || Date.now() - s.ts > PLAN_TTL_MS) return null;
    return { plan: s.plan, addedKeys: new Set(s.addedKeys ?? []), ts: s.ts };
  } catch {
    return null;
  }
}

function saveStoredPlan(
  day: string,
  plan: MyDayPlanResult,
  addedKeys: Set<string>,
  ts: number
): void {
  try {
    const s: StoredPlan = { day, plan, addedKeys: [...addedKeys], ts };
    localStorage.setItem(PLAN_STORAGE_KEY, JSON.stringify(s));
  } catch {
    /* ignore quota / serialization errors — the plan just won't persist */
  }
}

// Cluster backlog items by their short "why it slips" reason so identical
// reasons (the AI is steered toward a few standard phrasings) form one group
// with a single header, instead of repeating the reason on every row. Insertion
// order is preserved (first-seen reason first); empty reasons cluster last.
function groupBacklogByReason(
  items: MyDayPlanItem[]
): { reason: string; items: MyDayPlanItem[] }[] {
  const groups = new Map<string, MyDayPlanItem[]>();
  for (const it of items) {
    const key = it.reason.trim();
    const bucket = groups.get(key);
    if (bucket) bucket.push(it);
    else groups.set(key, [it]);
  }
  const out = [...groups.entries()].map(([reason, items]) => ({ reason, items }));
  // Push the "no reason given" bucket to the bottom.
  out.sort((a, b) => (a.reason === "" ? 1 : 0) - (b.reason === "" ? 1 : 0));
  return out;
}

// Returns true when a date string (YYYY-MM-DD or ISO) is strictly before today.
// Pure string comparison on the local calendar date — mixing a UTC-parsed
// date-only value with a local-midnight Date mislabels "today" as overdue in
// negative-UTC timezones.
function isOverdue(dateStr: string | null | undefined): boolean {
  if (!dateStr) return false;
  return dateStr.slice(0, 10) < localTodayIso();
}

export function Today(): preact.JSX.Element {
  const { day, items: storeItems, suggestions, loading } = useMyDay();
  const [, setLocation] = useLocation();
  // UX-Vorschlag P1: zweispaltiger Tagesplan mit „Braucht dich", Ziehen zum
  // Sortieren. Aus (classic + Flag „vorschau") bleibt alles wie bisher.
  const p1 = uxEnabled("ux_web_p1");
  // Optimistische Reihenfolge nach dem Ziehen, bis der Store neu geladen hat.
  const [orderOverride, setOrderOverride] = useState<number[] | null>(null);
  useEffect(() => setOrderOverride(null), [storeItems]);
  const items: MyDayItem[] = orderOverride
    ? applyReorder(storeItems, orderOverride)
    : storeItems;

  // KI-Tagesplanung (propose-only, on demand). Persisted for 4h (see PLAN_TTL_MS)
  // so a generated recommendation survives reloads; `planTs` is the generation
  // time that drives both persistence and expiry.
  const [plan, setPlan] = useState<MyDayPlanResult | null>(null);
  const [planTs, setPlanTs] = useState<number | null>(null);
  const [planning, setPlanning] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);
  // Plan items the user already pushed into "Heute" (client-side, so the button
  // can flip to a done state without re-fetching the whole plan).
  const [addedPlanKeys, setAddedPlanKeys] = useState<Set<string>>(new Set());

  const planKey = (p: MyDayPlanItem): string => `${p.kind}-${p.id}`;

  // Re-load when the component mounts so navigating back always shows fresh data.
  useEffect(() => {
    void refreshMyDay();
  }, []);

  // On day change (and initial mount): restore a still-fresh plan for this day
  // from storage, otherwise start clean. This is what lets a recommendation
  // survive a page reload for up to 4 hours.
  useEffect(() => {
    const stored = loadStoredPlan(day);
    if (stored) {
      setPlan(stored.plan);
      setAddedPlanKeys(stored.addedKeys);
      setPlanTs(stored.ts);
    } else {
      setPlan(null);
      setAddedPlanKeys(new Set());
      setPlanTs(null);
    }
    setPlanError(null);
  }, [day]);

  // Persist the current plan (and which items were pushed to "Heute") whenever it
  // changes, keeping the original generation time so the 4h window is stable.
  useEffect(() => {
    if (plan && planTs !== null) saveStoredPlan(day, plan, addedPlanKeys, planTs);
  }, [plan, addedPlanKeys, planTs, day]);

  // Open the task behind an item: project tasks deep-link into the board,
  // personal tasks into the lists view (mirrors MyTasks.onOpen). Signals, not
  // ?task= params — Project/Lists never parse a query param.
  const openFullItem = (kind: "task" | "personal", id: number, projectId: number | null): void => {
    if (kind === "task" && projectId !== null) {
      openProjectTaskSig.value = id;
      setLocation(`/p/${projectId}`);
    } else {
      openPersonalTaskSig.value = id;
      setLocation("/lists");
    }
  };

  // UX-Vorschlag P3: Eintraege des Tagesplans oeffnen im gemeinsamen
  // Detailbereich (↑/↓ wechselt). Vorschlaege/KI-Plan und classic oeffnen wie
  // bisher das Projekt bzw. die Liste.
  const p3 = uxEnabled("ux_web_p3");
  const [detailUid, setDetailUid] = useState<string | null>(null);
  const handleOpenItem = (kind: "task" | "personal", id: number, projectId: number | null): void => {
    const uid = `${kind === "task" ? "project" : "personal"}:${id}`;
    const inPlan = storeItems.some((i) =>
      kind === "task" ? i.task?.id === id : i.personal_task?.id === id
    );
    if (p3 && inPlan) setDetailUid(uid);
    else openFullItem(kind, id, projectId);
  };
  const openFullRecord = (r: TaskRecord): void =>
    openFullItem(r.source === "project" ? "task" : "personal", r.id, r.projectId);
  // „Braucht dich"/„Diese Woche": mit P1 + P3 im selben Detailbereich, statt die
  // Seite zu wechseln. Findet TodayDetail die Aufgabe nicht, geht es wie bisher
  // ins Projekt bzw. in die Liste.
  const sideFallback = useRef<{ kind: "task" | "personal"; id: number; projectId: number | null } | null>(null);
  const openFromSide =
    p1 && p3
      ? (kind: "task" | "personal", id: number, projectId: number | null): void => {
          sideFallback.current = { kind, id, projectId };
          setDetailUid(`${kind === "task" ? "project" : "personal"}:${id}`);
        }
      : undefined;
  const handleDetailMissing = (uid: string): void => {
    setDetailUid(null);
    const f = sideFallback.current;
    if (f && uid === `${f.kind === "task" ? "project" : "personal"}:${f.id}`) openFullItem(f.kind, f.id, f.projectId);
  };
  const detail =
    p3 && detailUid !== null ? (
      <TodayDetail
        items={items}
        uid={detailUid}
        onSelect={setDetailUid}
        onOpenFull={openFullRecord}
        onMissing={handleDetailMissing}
      />
    ) : null;

  // Same as handleOpenItem, specialized for a KI plan item (already has project_id resolved).
  const handleOpenPlanItem = (p: MyDayPlanItem): void => {
    handleOpenItem(p.kind, p.id, p.project_id);
  };

  // Add a plan item to "Heute" and mark it added (idempotent server-side).
  const handleAddPlanItem = async (p: MyDayPlanItem): Promise<void> => {
    const ok = await handleAdd(p.kind, p.id);
    // Only mark as added when the request actually succeeded.
    if (ok) setAddedPlanKeys((prev) => new Set(prev).add(planKey(p)));
  };

  const handlePlanDay = async (): Promise<void> => {
    setPlanning(true);
    setPlanError(null);
    setAddedPlanKeys(new Set());
    try {
      const result = await aiPlanMyDay(day);
      setPlan(result);
      setPlanTs(Date.now());
    } catch (e) {
      setPlanError(
        e instanceof ApiError && e.status === 503
          ? "KI-Funktionen sind nicht aktiviert."
          : "Tagesplanung fehlgeschlagen. Bitte später erneut versuchen."
      );
    } finally {
      setPlanning(false);
    }
  };

  const handleDateChange = (e: Event): void => {
    const v = (e.target as HTMLInputElement).value;
    if (v) setMyDayDate(v);
  };

  const handlePrevDay = (): void => {
    const d = new Date(day);
    d.setDate(d.getDate() - 1);
    setMyDayDate(d.toISOString().slice(0, 10));
  };

  const handleNextDay = (): void => {
    const d = new Date(day);
    d.setDate(d.getDate() + 1);
    setMyDayDate(d.toISOString().slice(0, 10));
  };

  // Errors are already toasted by the api() wrapper — the catch blocks only
  // prevent unhandled promise rejections from the fire-and-forget onClicks.
  const handleToggleDone = async (personalTaskId: number, currentDone: boolean): Promise<void> => {
    try {
      await togglePersonalTaskDone(personalTaskId, !currentDone);
      await refreshMyDay();
    } catch {
      /* toast via api() */
    }
  };

  const handleRemove = async (entryId: number): Promise<void> => {
    try {
      await removeFromMyDay(entryId);
    } catch {
      /* toast via api() */
    }
  };

  const handleDragEnd = async (result: DropResult): Promise<void> => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const ids = items.map((i) => i.entry_id);
    const [moved] = ids.splice(result.source.index, 1);
    ids.splice(result.destination.index, 0, moved!);
    setOrderOverride(ids);
    try {
      await reorderMyDay(day, ids);
      await refreshMyDay();
    } catch {
      // api() hat getoastet; Serverstand wiederherstellen.
      setOrderOverride(null);
    }
  };

  const handleAdd = async (kind: "task" | "personal", id: number): Promise<boolean> => {
    try {
      if (kind === "task") {
        await addToMyDay({ task_id: id, day });
      } else {
        await addToMyDay({ personal_task_id: id, day });
      }
      return true;
    } catch {
      /* toast via api() */
      return false;
    }
  };

  // Keys of everything that already sits in "Heute". A plan survives for 4h, so
  // an item added meanwhile (from the suggestion list, another device, …) would
  // otherwise keep being recommended — it is filtered out of the plan below.
  // Items the user pushed via the plan's OWN button stay visible so that button
  // can flip to "Hinzugefügt" instead of the row vanishing under the cursor.
  const todayKeys = new Set(
    items.map((i) =>
      i.kind === "personal"
        ? `personal-${i.personal_task?.id}`
        : `task-${i.task?.id}`
    )
  );
  const stillProposable = (p: MyDayPlanItem): boolean =>
    !todayKeys.has(planKey(p)) || addedPlanKeys.has(planKey(p));
  const planItems = plan ? plan.plan.filter(stillProposable) : [];
  const backlogItems = plan ? plan.backlog.filter(stillProposable) : [];

  // Format the stored day string for display in the header.
  // Wochentag + ausgeschriebener Monat: das Datumsfeld rechts zeigt das
  // Kurzdatum bereits — der Untertitel wiederholte es nur ("26.10.26").
  const displayDay = new Date(`${day}T00:00:00`).toLocaleDateString("de-DE", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });

  // Alle VIER Aktionen des alten Kopfes ziehen unverändert um: Tag zurück,
  // Datumsfeld, Tag vor, „Tag planen (KI)". Vorher vier, nachher vier.
  // Das Datumsfeld behält `text-base sm:text-xs` — unter 16px zoomt iOS beim
  // Fokus in ein Eingabefeld hinein (Projekt-Konvention).
  const doneCount = items.filter((i) => i.kind === "personal" && i.personal_task?.is_done).length;
  usePageHeader({
    title: requireLabel("/today"),
    subtitle:
      p1 && items.length > 0
        ? `${displayDay} · ${doneCount} von ${items.length} geplant erledigt`
        : displayDay,
    actions: (
      <>
        <Button
          variant="secondary"
          size="sm"
          icon={ChevronLeft}
          onClick={handlePrevDay}
          aria-label="Vorheriger Tag"
        />
        <input
          type="date"
          value={day}
          onInput={handleDateChange}
          class="min-h-[30px] rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-2 py-1 text-base text-[color:var(--color-on-surface)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 sm:text-xs"
          aria-label="Datum auswählen"
        />
        <Button
          variant="secondary"
          size="sm"
          icon={ChevronRight}
          onClick={handleNextDay}
          aria-label="Nächster Tag"
        />
        <Button
          variant="secondary"
          size="sm"
          icon={Sparkles}
          loading={planning}
          onClick={() => void handlePlanDay()}
          aria-label="Tag mit KI planen"
        >
          {planning ? "Plane…" : "Tag planen (KI)"}
        </Button>
      </>
    ),
  });

  return (
    <Layout>
      {/* `narrow` (768px) statt der Standardbreite: „Mein Tag" ist EINE
          einspaltige Tagesliste zum Abarbeiten von oben nach unten — es gibt
          hier kein zweites Panel und keine Tabelle, die Breite bräuchte. Die
          bisherigen 672px (max-w-2xl) waren die schmalste der sechs
          Bestandsbreiten; 768px ist die eine schmale Breite der neuen Hülle und
          hält die Zeilenlänge lesbar, statt die Häkchen 1440px weit von den
          Aktionen zu trennen. */}
      <PageShell width={p1 ? "default" : "narrow"}>
        {/* P1: zwei Spalten — links der Tagesplan (unveraenderte Abschnitte),
            rechts „Braucht dich" + „Diese Woche". Ohne P1 kein Wrapper-Effekt:
            `contents` laesst die Kinder direkt in der PageShell stehen. */}
        <div class={p1 ? "grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(320px,400px)] lg:items-start" : "contents"}>
        <div class={p1 ? "min-w-0" : "contents"}>

        {/* ---- Heute-Items ---- */}
        <section class="mb-8">
          <h2 class="text-xs font-semibold tracking-widest uppercase text-[color:var(--color-on-surface-variant)] mb-3">
            Heute
          </h2>
          {/* P1 + P2: Schnellerfassung direkt am Tagesplan (globaler Dialog). */}
          {p1 && uxEnabled("ux_web_p2") && (
            <button
              type="button"
              onClick={() => openCapture("", { myDay: day })}
              class="mb-2 flex w-full items-center gap-2 rounded border border-dashed border-[color:var(--color-outline-variant)] px-3 py-2 text-left text-sm text-[color:var(--color-on-surface-variant)] hover:border-[color:var(--color-primary)] hover:text-[color:var(--color-primary)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
            >
              <Plus size={14} aria-hidden="true" />
              <span class="flex-1">Aufgabe für heute … z. B. „Rückruf Schäfer 10 Uhr !hoch #Projekt"</span>
              <kbd class="rounded border border-[color:var(--color-outline-variant)] px-1 text-micro">N</kbd>
            </button>
          )}
          {loading ? (
            <p class="text-sm text-[color:var(--color-on-surface-variant)] italic">Lädt…</p>
          ) : items.length === 0 ? (
            <p class="text-sm text-[color:var(--color-on-surface-variant)] italic">
              Nichts für heute — füge etwas aus den Vorschlägen hinzu.
            </p>
          ) : (
            <TodayList p1={p1} onDragEnd={(r) => void handleDragEnd(r)}>
              {items.map((item, index) => {
                const isPersonal = item.kind === "personal";
                const title = isPersonal
                  ? item.personal_task?.title ?? "(Unbekannt)"
                  : item.task?.title ?? "(Unbekannt)";
                const dueStr = isPersonal
                  ? item.personal_task?.due_date ?? null
                  : item.task?.deadline ?? null;
                const isDone = isPersonal ? (item.personal_task?.is_done ?? false) : false;
                const overdue = !isDone && isOverdue(dueStr);
                const itemId = isPersonal ? item.personal_task?.id : item.task?.id;
                const projectId = isPersonal ? null : item.task?.project_id ?? null;

                return (
                  <TodayRow key={item.entry_id} p1={p1} entryId={item.entry_id} index={index} title={title}>
                    {/* Completion checkbox — personal tasks only */}
                    {isPersonal ? (
                      <button
                        type="button"
                        onClick={() =>
                          void handleToggleDone(item.personal_task!.id, isDone)
                        }
                        class={`shrink-0 inline-flex items-center justify-center rounded-full size-5 border-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 ${
                          isDone
                            ? "border-[color:var(--color-status-done)] bg-[color:var(--color-status-done)] text-[color:var(--color-on-primary)]"
                            : "border-[color:var(--color-outline-variant)] hover:border-[color:var(--color-primary)]"
                        }`}
                        aria-label={isDone ? "Als offen markieren" : "Als erledigt markieren"}
                      >
                        {isDone && <Check size={14} aria-hidden="true" />}
                      </button>
                    ) : (
                      <Circle
                        size={16}
                        class="shrink-0 text-[color:var(--color-on-surface-variant)]"
                        aria-hidden="true"
                      />
                    )}

                    {/* Title + meta — clickable to open the underlying task */}
                    <button
                      type="button"
                      onClick={() => itemId !== undefined && handleOpenItem(item.kind, itemId, projectId)}
                      disabled={itemId === undefined}
                      class="flex-1 min-w-0 rounded px-1.5 py-0.5 -mx-1.5 text-left hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] disabled:cursor-default transition-colors"
                      aria-label={`${title} öffnen`}
                    >
                      <span
                        class={`block text-sm ${
                          isDone
                            ? "line-through opacity-60 text-[color:var(--color-on-surface-variant)]"
                            : "text-[color:var(--color-on-surface)]"
                        }`}
                      >
                        {title}
                      </span>
                      <span class="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        <span
                          class={`inline-block rounded-full px-1.5 py-0 text-label font-medium border ${
                            isPersonal
                              ? "border-[color:var(--color-primary)]/30 text-[color:var(--color-primary-strong)] bg-[color:var(--color-primary)]/8"
                              : "border-[color:var(--color-outline-variant)] text-[color:var(--color-on-surface-variant)] bg-[color:var(--color-surface-container-low)]"
                          }`}
                        >
                          {isPersonal ? "Persönlich" : "Projektaufgabe"}
                        </span>
                        {dueStr && (
                          <span
                            class={`text-label ${
                              overdue
                                ? "text-[color:var(--color-error)] font-medium"
                                : "text-[color:var(--color-on-surface-variant)]"
                            }`}
                          >
                            {overdue ? "Überfällig: " : ""}{formatDateShort(dueStr)}
                          </span>
                        )}
                      </span>
                    </button>

                    {/* Remove button */}
                    <button
                      type="button"
                      onClick={() => void handleRemove(item.entry_id)}
                      class="shrink-0 inline-flex items-center justify-center rounded p-1 text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] hover:text-[color:var(--color-error)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors"
                      aria-label={`${title} aus Mein Tag entfernen`}
                    >
                      <X size={14} aria-hidden="true" />
                    </button>
                  </TodayRow>
                );
              })}
            </TodayList>
          )}
        </section>

        {/* ---- KI-Tagesplan (propose-only, on demand) ---- */}
        {(planError || planning || plan) && (
          <section class="mb-8">
            <h2 class="text-xs font-semibold tracking-widest uppercase text-[color:var(--color-on-surface-variant)] mb-3 flex items-center gap-1.5">
              <Sparkles size={14} aria-hidden="true" />
              KI-Empfehlung für heute
            </h2>
            {planError ? (
              <p class="text-sm text-[color:var(--color-error)]">{planError}</p>
            ) : planning ? (
              <p class="text-sm text-[color:var(--color-on-surface-variant)] italic">
                KI priorisiert deine Aufgaben…
              </p>
            ) : plan &&
              planItems.length === 0 &&
              backlogItems.length === 0 ? (
              <p class="text-sm text-[color:var(--color-on-surface-variant)] italic">
                Nichts mehr zu priorisieren — alles Offene steht schon in „Heute“.
              </p>
            ) : plan ? (
              <>
                {planItems.length > 0 && (
                  <p class="mb-1.5 text-micro font-medium uppercase tracking-wide text-[color:var(--color-on-surface-variant)]">
                    Heute machbar
                  </p>
                )}
                {/* empty:hidden — the bordered box would otherwise show as a bare
                    line when every "heute machbar" item is already in Heute. */}
                <ol class="empty:hidden rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] divide-y divide-[color:var(--color-outline-variant)]">
                  {planItems.map((p, idx) => {
                    const overdue = p.overdue;
                    const added = addedPlanKeys.has(planKey(p));
                    return (
                      <li key={`${p.kind}-${p.id}`} class="flex items-start gap-2 px-1.5 py-1">
                        {/* Clickable area — opens the underlying task */}
                        <button
                          type="button"
                          onClick={() => handleOpenPlanItem(p)}
                          class="flex flex-1 min-w-0 items-start gap-2.5 rounded px-1.5 py-1 text-left hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors"
                          aria-label={`${p.title} öffnen`}
                        >
                          <span class="shrink-0 mt-0.5 inline-flex items-center justify-center size-5 rounded-full bg-[color:var(--color-primary)]/12 text-label font-semibold text-[color:var(--color-primary-strong)]">
                            {idx + 1}
                          </span>
                          <span class="flex-1 min-w-0">
                            <span class="block text-sm text-[color:var(--color-on-surface)]">
                              {p.title}
                            </span>
                            {p.reason && (
                              <span class="block text-micro text-[color:var(--color-on-surface-variant)] mt-0.5">
                                {p.reason}
                              </span>
                            )}
                            <span class="flex items-center gap-1.5 mt-0.5 flex-wrap">
                              <span
                                class={`inline-block rounded-full px-1.5 py-0 text-label font-medium border ${
                                  p.kind === "personal"
                                    ? "border-[color:var(--color-primary)]/30 text-[color:var(--color-primary-strong)] bg-[color:var(--color-primary)]/8"
                                    : "border-[color:var(--color-outline-variant)] text-[color:var(--color-on-surface-variant)] bg-[color:var(--color-surface-container-low)]"
                                }`}
                              >
                                {p.kind === "personal" ? "Persönlich" : "Projektaufgabe"}
                              </span>
                              {p.due_date && (
                                <span
                                  class={`text-label ${
                                    overdue
                                      ? "text-[color:var(--color-error)] font-medium"
                                      : "text-[color:var(--color-on-surface-variant)]"
                                  }`}
                                >
                                  {overdue ? "Überfällig: " : ""}
                                  {formatDateShort(p.due_date)}
                                </span>
                              )}
                            </span>
                          </span>
                        </button>
                        {/* Add to "Heute" */}
                        <button
                          type="button"
                          onClick={() => void handleAddPlanItem(p)}
                          disabled={added}
                          class="mt-1 shrink-0 inline-flex items-center gap-1 rounded border border-[color:var(--color-primary)]/40 bg-[color:var(--color-primary)]/8 px-2 py-1 text-label font-medium text-[color:var(--color-primary-strong)] hover:bg-[color:var(--color-primary)]/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 disabled:opacity-60 transition-colors"
                          aria-label={
                            added
                              ? `${p.title} ist zu Heute hinzugefügt`
                              : `${p.title} zu Heute hinzufügen`
                          }
                        >
                          {added ? (
                            <>
                              <Check size={14} aria-hidden="true" />
                              Hinzugefügt
                            </>
                          ) : (
                            <>
                              <Plus size={14} aria-hidden="true" />
                              Zu Heute
                            </>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ol>

                {/* ---- Bleibt heute liegen (structured backlog) ---- */}
                {backlogItems.length > 0 && (
                  <div class="mt-4 rounded border border-[color:var(--color-warning)]/30 bg-[color:var(--color-warning)]/8">
                    <div class="flex items-center gap-1.5 px-3 pt-2.5 pb-1">
                      <AlertTriangle
                        size={14}
                        class="shrink-0 text-[color:var(--color-warning)]"
                        aria-hidden="true"
                      />
                      <span class="text-micro font-semibold uppercase tracking-wide text-[color:var(--color-warning)]">
                        Bleibt heute liegen
                      </span>
                      <span class="text-micro font-medium text-[color:var(--color-on-surface-variant)]">
                        ({backlogItems.length})
                      </span>
                    </div>
                    {plan.backlog_note && (
                      <p class="px-3 pb-2 text-xs leading-snug text-[color:var(--color-on-surface-variant)]">
                        {plan.backlog_note}
                      </p>
                    )}
                    <div class="px-1.5 pb-1.5">
                      {groupBacklogByReason(backlogItems).map((g) => (
                        <div key={g.reason} class="mt-1 first:mt-0">
                          <p class="px-1.5 pt-1 pb-0.5 text-micro font-medium text-[color:var(--color-on-surface-variant)]">
                            {g.reason || "Nicht heute eingeplant"}
                          </p>
                          <ul>
                            {g.items.map((p) => {
                              const added = addedPlanKeys.has(planKey(p));
                              return (
                                <li
                                  key={`${p.kind}-${p.id}`}
                                  class="flex items-center gap-1.5 rounded px-1 py-0.5"
                                >
                                  <button
                                    type="button"
                                    onClick={() => handleOpenPlanItem(p)}
                                    class="flex flex-1 min-w-0 items-center gap-2 rounded px-1.5 py-1 text-left hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] transition-colors"
                                    aria-label={`${p.title} öffnen`}
                                  >
                                    <span
                                      class="shrink-0 size-1.5 rounded-full bg-[color:var(--color-on-surface-variant)]/50"
                                      aria-hidden="true"
                                    />
                                    <span class="flex-1 min-w-0 truncate text-sm text-[color:var(--color-on-surface)]">
                                      {p.title}
                                    </span>
                                    {p.due_date && (
                                      <span
                                        class={`shrink-0 text-label ${
                                          p.overdue
                                            ? "text-[color:var(--color-error)] font-medium"
                                            : "text-[color:var(--color-on-surface-variant)]"
                                        }`}
                                      >
                                        {p.overdue ? "Überfällig: " : ""}
                                        {formatDateShort(p.due_date)}
                                      </span>
                                    )}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => void handleAddPlanItem(p)}
                                    disabled={added}
                                    class="shrink-0 inline-flex items-center gap-1 rounded border border-[color:var(--color-outline-variant)] px-1.5 py-0.5 text-label font-medium text-[color:var(--color-on-surface-variant)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 disabled:opacity-60 transition-colors"
                                    aria-label={
                                      added
                                        ? `${p.title} ist zu Heute hinzugefügt`
                                        : `${p.title} trotzdem zu Heute hinzufügen`
                                    }
                                  >
                                    {added ? (
                                      <Check size={14} aria-hidden="true" />
                                    ) : (
                                      <Plus size={14} aria-hidden="true" />
                                    )}
                                    {added ? "Hinzugefügt" : "Heute"}
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </section>
        )}

        {/* ---- Vorschläge ---- */}
        {!loading && suggestions.length > 0 && (
          <section>
            <h2 class="text-xs font-semibold tracking-widest uppercase text-[color:var(--color-on-surface-variant)] mb-3">
              Vorschläge
            </h2>
            <ul class="rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] divide-y divide-[color:var(--color-outline-variant)]">
              {suggestions.map((s, idx) => {
                const isPersonal = s.kind === "personal";
                const id = isPersonal ? s.personal_task?.id : s.task?.id;
                const title = isPersonal
                  ? s.personal_task?.title ?? "(Unbekannt)"
                  : s.task?.title ?? "(Unbekannt)";
                const dueStr = isPersonal
                  ? s.personal_task?.due_date ?? null
                  : s.task?.deadline ?? null;
                const overdue = isOverdue(dueStr);
                const projectId = isPersonal ? null : s.task?.project_id ?? null;

                return (
                  <li key={`${s.kind}-${id ?? idx}`} class="flex items-center gap-2 px-3 py-2">
                    <Circle
                      size={16}
                      class="shrink-0 text-[color:var(--color-on-surface-variant)]"
                      aria-hidden="true"
                    />
                    <button
                      type="button"
                      onClick={() => id !== undefined && handleOpenItem(s.kind, id, projectId)}
                      disabled={id === undefined}
                      class="flex-1 min-w-0 rounded px-1.5 py-0.5 -mx-1.5 text-left hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] disabled:cursor-default transition-colors"
                      aria-label={`${title} öffnen`}
                    >
                      <span class="block text-sm text-[color:var(--color-on-surface)]">{title}</span>
                      <span class="flex items-center gap-1.5 mt-0.5 flex-wrap">
                        <span
                          class={`inline-block rounded-full px-1.5 py-0 text-label font-medium border ${
                            isPersonal
                              ? "border-[color:var(--color-primary)]/30 text-[color:var(--color-primary-strong)] bg-[color:var(--color-primary)]/8"
                              : "border-[color:var(--color-outline-variant)] text-[color:var(--color-on-surface-variant)] bg-[color:var(--color-surface-container-low)]"
                          }`}
                        >
                          {isPersonal ? "Persönlich" : "Projektaufgabe"}
                        </span>
                        {dueStr && (
                          <span
                            class={`text-label ${
                              overdue
                                ? "text-[color:var(--color-error)] font-medium"
                                : "text-[color:var(--color-on-surface-variant)]"
                            }`}
                          >
                            {overdue ? "Überfällig: " : ""}{formatDateShort(dueStr)}
                          </span>
                        )}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => id !== undefined && void handleAdd(s.kind, id)}
                      disabled={id === undefined}
                      class="shrink-0 inline-flex items-center gap-1 rounded border border-[color:var(--color-primary)]/40 bg-[color:var(--color-primary)]/8 px-2 py-1 text-label font-medium text-[color:var(--color-primary-strong)] hover:bg-[color:var(--color-primary)]/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 disabled:opacity-40 transition-colors"
                      aria-label={`${title} zu Mein Tag hinzufügen`}
                    >
                      <Plus size={14} aria-hidden="true" />
                      Hinzufügen
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
        </div>
        {p1 && (
          <aside class="min-w-0 lg:sticky lg:top-4">
            <NeedsYouPanel day={day} todayKeys={todayKeys} onAddToday={handleAdd} onOpenTask={openFromSide} />
          </aside>
        )}
        </div>
        {/* Detail als Inlay (#1254), auch neben „Braucht dich" (P1). */}
        {detail && <DetailSheet onClose={() => setDetailUid(null)}>{detail}</DetailSheet>}
      </PageShell>
    </Layout>
  );
}

// ---------- P1: Liste mit optionalem Ziehen ----------
// Ohne P1 rendern beide Helfer exakt das bisherige Markup (<ul>/<li> mit den
// alten Klassen) — classic sieht keinen Unterschied.

const LIST_CLASS =
  "rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] divide-y divide-[color:var(--color-outline-variant)]";
const ROW_CLASS = "flex items-center gap-2 px-3 py-2";

function TodayList(props: {
  p1: boolean;
  onDragEnd: (r: DropResult) => void;
  children: preact.ComponentChildren;
}): preact.JSX.Element {
  if (!props.p1) return <ul class={LIST_CLASS}>{props.children}</ul>;
  return (
    <DragDropContext onDragEnd={props.onDragEnd}>
      <Droppable droppableId="my-day-today">
        {(provided) => (
          <ul ref={provided.innerRef} {...provided.droppableProps} class={LIST_CLASS} aria-label="Heute — ziehen zum Sortieren">
            {props.children}
            {provided.placeholder}
          </ul>
        )}
      </Droppable>
    </DragDropContext>
  );
}

function TodayRow(props: {
  p1: boolean;
  entryId: number;
  index: number;
  title: string;
  children: preact.ComponentChildren;
}): preact.JSX.Element {
  if (!props.p1) return <li class={ROW_CLASS}>{props.children}</li>;
  return (
    <Draggable draggableId={`my-day-${props.entryId}`} index={props.index}>
      {(provided, snapshot) => (
        <li
          ref={provided.innerRef}
          {...(provided.draggableProps as unknown as Record<string, unknown>)}
          style={provided.draggableProps.style as preact.JSX.CSSProperties | undefined}
          class={`${ROW_CLASS} bg-[color:var(--color-surface-container-lowest)]${
            snapshot.isDragging ? " shadow-level-3" : ""
          }`}
        >
          <span
            {...((provided.dragHandleProps ?? undefined) as Record<string, unknown> | undefined)}
            class="-ml-1 shrink-0 cursor-grab rounded p-0.5 text-[color:var(--color-on-surface-variant)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]"
            aria-label={`${props.title} verschieben`}
          >
            <GripVertical size={14} aria-hidden="true" />
          </span>
          {props.children}
        </li>
      )}
    </Draggable>
  );
}
